/**
 * Public Terms of Service page (/shop/terms).
 *
 * TCGMA-175 / TCGMA-180 — the kiosk booth welcome email confirms acceptance of
 * these terms and links here, so the page must exist and be publicly reachable.
 *
 * IMPORTANT — THIS IS A DRAFT, NOT LEGAL ADVICE. It's a reasonable starting
 * point tailored to a trading-card resale storefront + trade-show booth, but it
 * MUST be reviewed by a lawyer and customized before it's treated as legally
 * operative. Fill in every [BRACKETED] placeholder (legal business name, state,
 * contact email, privacy-policy link, currency, age). Edit the TERMS array
 * below or swap the body wholesale.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";

export const Route = createFileRoute("/shop/terms")({
  component: ShopTermsPage,
});

const LAST_UPDATED = "August 2026";

const TERMS: { heading: string; body: string }[] = [
  {
    heading: "1. Agreement to these Terms",
    body: "These Terms of Service (the “Terms”) are a binding agreement between you and [LEGAL BUSINESS NAME] (“[SHORT NAME],” “we,” “us,” or “our”), which operates the storefront at [thenooblounge.com] and sells trading cards at trade shows and events. By creating an account, signing up at our booth, entering a raffle, or placing an order, you agree to these Terms and to our Privacy Policy. If you do not agree, please do not use our services.",
  },
  {
    heading: "2. Eligibility",
    body: "You must be at least [18] years old, or the age of majority where you live, to purchase from us or create an account. If you are a minor, you may use our services only with the involvement of a parent or guardian who agrees to these Terms. You are responsible for making sure your use complies with the laws that apply to you.",
  },
  {
    heading: "3. Your account and sign-in",
    body: "We use passwordless, one-time email sign-in links — there is no password to set or lose. Keep access to your email secure, because anyone with a valid sign-in link can access your account. Sign-in links expire and can be used only once. Tell us promptly if you believe your account or email has been accessed without your permission, and please keep the information you give us accurate and up to date.",
  },
  {
    heading: "4. Products, condition, and availability",
    body: "We sell individual and bundled trading cards and related collectibles, most of which are single, pre-owned items. We describe card condition using standard grades (for example, Near Mint, Lightly Played, Moderately Played) and, where applicable, third-party grading designations. Photographs and descriptions are provided in good faith, and minor variation is normal for pre-owned collectibles. Because much of our stock is one-of-a-kind, an item may sell or become unavailable before your order is confirmed.",
  },
  {
    heading: "5. Pricing, orders, and payment",
    body: "Prices are shown in [USD] and may change at any time. We make reasonable efforts to price and describe items accurately, but errors happen; where an item’s price or description is materially wrong, we may cancel the affected order and refund any payment, even after the order is placed. Applicable taxes and shipping are calculated at checkout, and payment is due when you place your order. Your order is an offer that we may accept or decline.",
  },
  {
    heading: "6. Shipping and delivery",
    body: "We ship to the addresses and regions shown at checkout. Delivery timeframes are estimates, not guarantees. Unless the law provides otherwise, title and risk of loss pass to you once we hand the items to the carrier. Please make sure your shipping address is correct — we are not responsible for items delivered to an incorrect address that you provided.",
  },
  {
    heading: "7. Returns, refunds, and cancellations",
    body: "Our returns and cancellation policy — including any time limits, restocking terms, and the condition items must be returned in — is presented at or before checkout and forms part of these Terms. Because condition drives a collectible’s value, returned items must arrive in the same condition in which they were described and shipped. If you believe a card’s condition or grade was misrepresented, please contact us promptly with photos so we can review it.",
  },
  {
    heading: "8. Trade-show signups and raffles",
    body: "When you sign up at our booth or enter a raffle, you give us your contact details so we can follow up and, if you opt in, send you updates. Any raffle we run is a FREE entry — no purchase is necessary to enter or win, and buying something does not improve your chances. Raffles are subject to the official rules posted at the event, which govern eligibility, entry, winner selection, and prizes, and are void where prohibited by law.",
  },
  {
    heading: "9. Email and communications",
    body: "We send transactional messages you request or that are necessary to provide our services (for example, sign-in links and order updates). We send marketing or newsletter email only to people who have opted in, and you can unsubscribe at any time using the link in those emails or by contacting us. Message and data rates may apply to messages you receive.",
  },
  {
    heading: "10. Privacy",
    body: "How we collect and use your personal information is described in our Privacy Policy at /shop/privacy, which is incorporated into these Terms. Please review it to understand how we handle your data.",
  },
  {
    heading: "11. Acceptable use",
    body: "You agree not to use our services to break the law or infringe others’ rights; to interfere with or disrupt the services or their security; to use bots, scrapers, or other automated means except as we expressly permit; to misrepresent your identity; or to attempt to gain unauthorized access to any account or system. We may suspend or end access for conduct that violates these Terms.",
  },
  {
    heading: "12. Intellectual property and trademarks",
    body: "Trading-card game names, logos, and card images are the property of their respective owners (including, as applicable, Nintendo, Creatures Inc., GAME FREAK Inc., The Pokémon Company, and Wizards of the Coast). We are an independent reseller of genuine, pre-owned cards and are not affiliated with, sponsored by, or endorsed by those companies; their marks are used only to identify the products we sell. Our own site content, branding, and layout are our property and may not be copied without permission.",
  },
  {
    heading: "13. Authenticity and grading",
    body: "We describe items as genuine to the best of our knowledge and stand behind their authenticity as described. Where a third-party grade (for example, PSA, CGC, or BGS) is noted, that grade was assigned by the grading company — not by us — and reflects their opinion at the time of grading. Descriptions of ungraded (“raw”) cards are our own good-faith assessment and are not a professional grade.",
  },
  {
    heading: "14. Disclaimers",
    body: "Except as expressly stated and to the fullest extent permitted by law, the services and items are provided “as is” and “as available,” without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. Some places do not allow certain warranty exclusions, so parts of this section may not apply to you.",
  },
  {
    heading: "15. Limitation of liability",
    body: "To the fullest extent permitted by law, [SHORT NAME] and its owners and staff will not be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits or data, arising out of or relating to your use of the services. To the extent liability cannot be excluded, our total liability for any claim is limited to the amount you paid for the item or order that gave rise to the claim. Some places do not allow these limits, so they may not apply to you.",
  },
  {
    heading: "16. Indemnification",
    body: "You agree to indemnify and hold harmless [SHORT NAME] and its owners and staff from claims, losses, and expenses (including reasonable legal fees) arising out of your misuse of the services or your violation of these Terms or applicable law.",
  },
  {
    heading: "17. Governing law and disputes",
    body: "These Terms are governed by the laws of the State of [STATE], without regard to its conflict-of-laws rules. Any dispute will be resolved in the state or federal courts located in [COUNTY, STATE], and you consent to their jurisdiction — except where applicable law gives you the right to bring a claim elsewhere.",
  },
  {
    heading: "18. Changes to these Terms",
    body: "We may update these Terms from time to time. When we do, we will revise the “last updated” date above. Material changes take effect when posted, and your continued use of the services after that means you accept the revised Terms.",
  },
  {
    heading: "19. Contact us",
    body: "Questions about these Terms? Contact us at [support@thenooblounge.com] or the address listed on our storefront.",
  },
];

function ShopTermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/shop"
        className="mb-6 inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to shop
      </Link>

      <h1 className="text-2xl font-semibold text-neutral-900">
        Terms of Service
      </h1>
      <p className="mt-1 text-sm text-neutral-500">
        Last updated: {LAST_UPDATED}
      </p>
      <p className="mt-4 text-sm leading-relaxed text-neutral-700">
        These Terms govern your use of our online storefront and our trade-show
        booths, including signups, raffle entries, and purchases. Please read
        them carefully.
      </p>

      <div className="mt-8 space-y-6">
        {TERMS.map((s) => (
          <section key={s.heading}>
            <h2 className="text-base font-semibold text-neutral-900">
              {s.heading}
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-700">
              {s.body}
            </p>
          </section>
        ))}
      </div>
    </div>
  );
}
